// Réception des travaux (15/10/2026) : date, réserves, procès-verbal facultatif. Point de départ des durées de conservation
// (historique et photos : 10 ans par défaut ; temps passé nominatif : 3 ans). Ajout seul : une correction est une nouvelle ligne
// motivée ; l'historique des corrections reste lisible. Déclarée par le chargé d'affaires, le référent technique ou l'administrateur.
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, FileText } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../../lib/api.ts';
import { date, dateHeure } from '../../../lib/format.ts';
import { Alerte, Bouton, Champ, ChampTexte, Dialogue, useNotifier } from '../../../components/ui.tsx';
import type { VueChantier } from './types.ts';

const aujourdhui = () => new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date());

function DialogueReception({ v, fermer }: { v: VueChantier; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const actuelle = v.reception.actuelle;
  const [jour, setJour] = useState(actuelle?.date ?? aujourdhui());
  const [reserves, setReserves] = useState(actuelle?.avec_reserves ?? false);
  const [note, setNote] = useState('');
  const [motif, setMotif] = useState('');
  const [piece, setPiece] = useState<File | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  // Sur téléphone, le message d'erreur (en haut du dialogue) est ramené dans la vue : jamais un refus invisible.
  const haut = useRef<HTMLDivElement>(null);
  useEffect(() => { if (erreur || aConfirmer) haut.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [erreur, aConfirmer]);
  const pret = !!jour && (!actuelle || motif.trim().length >= 5);
  const envoyer = async (confirmer = false) => {
    setErreur(null); setEnvoi(true);
    try {
      const f = new FormData();
      f.set('date', jour);
      f.set('avecReserves', reserves ? 'true' : 'false');
      if (note.trim()) f.set('note', note.trim());
      if (actuelle) { f.set('remplace', actuelle.id); f.set('motif', motif.trim()); }
      if (confirmer) f.set('confirmerEcheance', 'true');
      if (piece) f.set('piece', piece, piece.name);
      const n = await api.envoyer<VueChantier>(`/api/chantiers/${v.chantier.id}/reception`, f);
      qc.setQueryData(['chantier', v.chantier.id], n);
      notifier(actuelle ? 'Réception corrigée : la précédente reste dans l’historique.' : 'Réception des travaux enregistrée.');
      fermer();
    } catch (x) {
      if (x instanceof ErreurApi && x.code === 'echeance_atteinte') setAConfirmer(x.message);
      else setErreur(messageErreur(x));
    } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={actuelle ? 'Corriger la réception des travaux' : 'Réception des travaux'} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton>
        {aConfirmer
          ? <Bouton variante="danger" chargement={envoi} onClick={() => envoyer(true)}>Confirmer cette date</Bouton>
          : <Bouton variante="principal" icone={<CalendarCheck aria-hidden />} chargement={envoi} disabled={!pret} onClick={() => envoyer(false)}>Enregistrer</Bouton>}</>}>
      <div className="pile" ref={haut}>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {aConfirmer && <Alerte ton="ambre" titre="Durée de conservation déjà écoulée">{aConfirmer}</Alerte>}
        <Champ libelle="Date de réception (procès-verbal)" type="date" value={jour} min={v.reception.premier_jour} max={aujourdhui()}
          aide={`Entre le début du chantier (${date(v.reception.premier_jour)}) et aujourd’hui.`} onChange={(x) => { setJour(x.target.value); setAConfirmer(null); }} />
        <label className="case"><input type="checkbox" checked={reserves} onChange={(x) => setReserves(x.target.checked)} /><span>Réception prononcée avec réserves</span></label>
        <ChampTexte libelle="Note (facultatif)" value={note} onChange={(x) => setNote(x.target.value)} maxLength={1000} rows={2} />
        <Champ libelle="Procès-verbal (facultatif : PDF ou photo, 10 Mo au plus)" type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={(x) => setPiece(x.target.files?.[0] ?? null)} />
        {actuelle && <ChampTexte libelle="Motif de la correction (obligatoire)" value={motif} onChange={(x) => setMotif(x.target.value)} maxLength={1000} rows={2}
          aide={`Remplace la réception du ${date(actuelle.date)} saisie par ${actuelle.par} ; elle reste dans l’historique.`} />}
        <p className="tres-petit discret" style={{ margin: 0 }}>Point de départ des durées de conservation : photos et historique nominatif de ce chantier gardés
          10 ans après la réception (par défaut), temps passé nominatif 3 ans. Rien n’est effacé dans les 30 jours qui suivent une saisie ou une
          correction (délai de garde). Saisie tracée dans le journal d’audit.</p>
      </div>
    </Dialogue>
  );
}

/** Où en est une catégorie : gardée jusqu'au …, durée écoulée (prochain passage), ou déjà traitée le …. */
function etatConservation(quoi: string, echeance: string, aujourdhui: string, fait: string | null, effet: string, s = '') {
  if (fait) return `${quoi} : ${effet} le ${date(fait)}`;
  if (echeance <= aujourdhui) return `${quoi} : durée écoulée depuis le ${date(echeance)}, traité${s} au prochain passage`;
  return `${quoi} gardé${s} jusqu’au ${date(echeance)}`;
}

/** Ligne « Réception des travaux » de la fiche du chantier. */
export function FicheReception({ v }: { v: VueChantier }) {
  const [ouvert, setOuvert] = useState(false);
  const r = v.reception;
  const a = r.actuelle;
  return (
    <div className="ch-fiche-large">
      <dt>Réception des travaux</dt>
      <dd className="ch-reception">
        {a ? <span>Le {date(a.date)}{a.avec_reserves ? ', avec réserves' : ', sans réserve'}
          <span className="petit discret"> — saisie par {a.par} le {dateHeure(a.le)}{a.motif ? ` (correction : ${a.motif})` : ''}</span></span>
          : <span className="secondaire">{r.possible ? 'Non déclarée : rien de l’historique de ce chantier ne sera effacé tant qu’elle n’est pas saisie.' : 'Après le lancement du chantier.'}</span>}
        {v.chantier.demo && <span className="petit discret">Démonstration : ses données ne sont jamais effacées par la conservation.</span>}
        {a?.note && <span className="petit">{a.note}</span>}
        {a?.piece && <a className="petit" href={`/api/chantiers/${v.chantier.id}/reception/${a.id}/piece`} target="_blank" rel="noopener">
          <FileText size={14} aria-hidden /> {a.piece.nom}</a>}
        {(r.echeances.historique || r.echeances.temps) && (
          <span className="petit discret">Conservation : {[
            r.echeances.temps && (r.echeances.temps <= r.aujourdhui && !r.traite.temps_nominatif
              ? (r.traite.temps_le ? `temps passé : agrégé sans nom le ${date(r.traite.temps_le)}` : 'temps passé : aucune déclaration nominative')
              : etatConservation('temps passé nominatif', r.echeances.temps, r.aujourdhui, null, 'agrégé sans nom')),
            r.echeances.historique && etatConservation('photos et noms', r.echeances.historique, r.aujourdhui, r.traite.historique_le,
              'photos effacées, noms remplacés par « Ancien salarié »', 's'),
          ].filter(Boolean).join(' ; ')}.</span>
        )}
        {r.garde_jusquau && r.garde_jusquau > r.aujourdhui && !v.chantier.demo && (
          <span className="petit discret">Délai de garde : rien de ce chantier n’est effacé avant le {date(r.garde_jusquau)} (une date erronée se corrige d’ici là).</span>
        )}
        {r.historique.length > 1 && (
          <details className="petit"><summary>Historique ({r.historique.length})</summary>
            <ul className="cons-liste">{r.historique.map((x) => <li key={x.id}>{date(x.date)} — {x.par}, le {dateHeure(x.le)}{x.motif ? ` — ${x.motif}` : ''}</li>)}</ul>
          </details>
        )}
        {v.droits.receptionner && (
          <span className="ch-reception-actions">
            <Bouton compact icone={<CalendarCheck aria-hidden />} onClick={() => setOuvert(true)}>{a ? 'Corriger la réception' : 'Saisir la réception'}</Bouton>
          </span>
        )}
      </dd>
      {ouvert && <DialogueReception v={v} fermer={() => setOuvert(false)} />}
    </div>
  );
}
